/**
 * Getting a settings anchor onto the screen, and keeping it there.
 *
 * Its own module because arriving at an anchor turned out to be three problems,
 * none of which is the layout's business, and all of which fail by doing
 * nothing rather than by throwing:
 *
 * 1. This runs from a ref callback, which React invokes while it is still
 *    mutating the DOM. At that moment the page is one section tall and nothing
 *    is scrollable, so a scroll issued there has nowhere to go.
 * 2. A smooth `scrollIntoView` can be silently dropped. Measured 2026-08-22 on
 *    a quiet settings page with nothing rendering: `behavior: "smooth"` left
 *    `scrollTop` at 0 while `behavior: "auto"` moved it to 1263 in the same
 *    frame.
 * 3. The sections above the target fetch their own data and expand afterwards.
 *    General grows from roughly 2000px to 4029px as Diagnostics resolves into
 *    eight sub-sections, which pushes a correct jump 2800px off screen.
 *
 * All three were invisible until the settings collapse, because every anchor
 * used to sit near the top of a short single-section page -- where not moving
 * *was* the correct outcome, and so was indistinguishable from these.
 *
 * The answer to all three is the same: converge instead of aiming once. Every
 * 50ms, if the target is not fully visible, pull it back. Stop as soon as it
 * has held still, and stop unconditionally when the reader takes over.
 *
 * @module
 */

/** Gap between correction passes. Short enough to look instant, long enough to be cheap. */
const SETTLE_INTERVAL_MS = 50;

/**
 * Passes to keep converging before giving up.
 *
 * ~4 seconds, which is what the real pages need rather than a round number:
 * General is still growing three seconds after the jump lands. Safe to be
 * generous because any scroll by the reader ends it immediately -- the window
 * only matters while they are waiting for the page anyway.
 */
const MAX_SETTLE_PASSES = 80;

/**
 * Passes the target must stay put, AND the page stop growing, before we stop.
 *
 * Being on screen is not enough on its own. Measured 2026-08-22: an `#about`
 * jump satisfied "visible" three passes in a row while General was still one
 * section tall, stopped watching 150ms in, and was then pushed 2800px down as
 * Diagnostics expanded. Visible-and-not-growing is the condition that actually
 * means arrived; visible alone means arrived *so far*.
 */
const STABLE_PASSES_REQUIRED = 6;

/** The nearest ancestor that can actually scroll, or null when nothing can. */
function nearestScrollableAncestor(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/**
 * Whether the reader can see where the target starts.
 *
 * The top edge, not the whole box: a settings section is routinely taller than
 * the pane it scrolls in, so "entirely on screen" is a condition it can never
 * satisfy -- and a jump that waits for it never reports arriving. Landing on a
 * heading is what the reader asked for anyway.
 *
 * Measured against the scroller rather than the window: settings scroll inside
 * a pane, so a row can be within the window and still below the pane.
 */
function isTargetOnScreen(element: HTMLElement): boolean {
  const scroller = nearestScrollableAncestor(element);
  const box = element.getBoundingClientRect();
  if (!scroller) {
    return box.top >= 0 && box.top < window.innerHeight;
  }
  const view = scroller.getBoundingClientRect();
  return box.top >= view.top && box.top < view.bottom;
}

/**
 * Pull `scrollTarget` into view every frame until it stays there.
 *
 * The first attempt asks for smooth, so the jump reads as movement where the
 * browser honours it. Corrections are instant: by then we already know the
 * target is in the wrong place, and animating a correction just means it is in
 * the wrong place for longer.
 */
function settleIntoView(
  scrollTarget: HTMLElement,
  prefersReducedMotion: boolean,
  onSettled: () => void,
): void {
  let passes = 0;
  let stablePasses = 0;
  let lastScrollHeight = -1;
  let stopped = false;
  let timer = 0;
  const userTookOver = ["wheel", "touchstart", "keydown"] as const;

  const stop = (settled: boolean): void => {
    if (stopped) return;
    stopped = true;
    window.clearTimeout(timer);
    for (const event of userTookOver) window.removeEventListener(event, stopUnsettled);
    // Only report success. The hash is what lets the browser's own fragment
    // handling finish the job if ours could not, so giving up must leave it in
    // place rather than clearing it on the way out.
    if (settled) onSettled();
  };
  const stopUnsettled = () => stop(false);
  for (const event of userTookOver)
    window.addEventListener(event, stopUnsettled, { passive: true });

  /** True once the target is where we want it and the page has stopped moving. */
  const hasArrived = (): boolean => {
    const scroller = nearestScrollableAncestor(scrollTarget);
    const scrollHeight = scroller?.scrollHeight ?? document.documentElement.scrollHeight;
    // The page growing under us is what invalidates a good jump, so it counts
    // against arrival exactly as a moved target does.
    const stillGrowing = scrollHeight !== lastScrollHeight;
    lastScrollHeight = scrollHeight;
    return isTargetOnScreen(scrollTarget) && !stillGrowing;
  };

  const tick = () => {
    if (stopped) return;
    if (!scrollTarget.isConnected) return stop(false);

    if (hasArrived()) {
      stablePasses += 1;
      if (stablePasses >= STABLE_PASSES_REQUIRED) return stop(true);
    } else {
      stablePasses = 0;
      scrollTarget.scrollIntoView({
        // Smooth only on the very first attempt, and only when it is wanted:
        // every later pass is a correction to a jump that already went wrong.
        behavior: passes === 0 && !prefersReducedMotion ? "smooth" : "auto",
        block: "center",
      });
    }

    passes += 1;
    if (passes >= MAX_SETTLE_PASSES) return stop(false);
    timer = window.setTimeout(tick, SETTLE_INTERVAL_MS);
  };

  // A timer, not requestAnimationFrame: rAF does not fire in a hidden tab, so
  // an anchor jump in a background tab would never converge -- and neither
  // would any automated check of this behaviour, which is how the first two
  // attempts at this fix looked like they had changed nothing.
  timer = window.setTimeout(tick, 0);
}

/** Jump to a settings row or section, and hold it on screen while the page settles. */
export function scrollAndFocusSettingsTarget(
  target: HTMLElement,
  onSettled: () => void = () => undefined,
): void {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // A section's own box starts above its heading, so scrolling the section
  // centres whitespace; its first child is the header row the reader wants.
  const scrollTarget =
    target.tagName === "SECTION" && target.firstElementChild
      ? (target.firstElementChild as HTMLElement)
      : target;

  settleIntoView(scrollTarget, prefersReducedMotion, onSettled);

  target.focus({ preventScroll: true });
  target.classList.remove("settings-search-target-pulse");
  if (prefersReducedMotion) return;
  // Reading offsetWidth restarts the animation when the same row is targeted
  // twice in a row; without it the second jump lands silently.
  void target.offsetWidth;
  target.classList.add("settings-search-target-pulse");
  // The class also suppresses the focus outline (the pulse is the destination
  // indicator), so drop it once the element is no longer the destination.
  target.addEventListener("blur", () => target.classList.remove("settings-search-target-pulse"), {
    once: true,
  });
}
